import React, { useState, useEffect, useMemo } from 'react';
import { Skeleton, Avatar, Empty, Tabs } from 'antd';
import { withTranslation } from 'react-i18next';
import SearchableTable from './SearchableTable';
import {
  ControlCenter,
  dummyCallback,
  PrintingCenter,
  PrintingQueue,
} from '../../../../utils/complexDocumentConstants';
import ReusableModal from '../../../../components/ReusableComponents/ReusableModal';
import {
  JobQueueContainer,
  CardTitle,
  JobQueueCard,
  BatchList,
  TaglistDetails,
  PrintBatchDetails,
  SearchExclusionFilters,
} from './styles';
import {
  getChannelTotals,
  getChannelUnknownCount,
  isEmptyObject,
  transformExclusionAndSearchKey,
  updateFunnelChartData,
} from '../../../../utils/helper';
import {
  fetchExclusionsData,
  getExclusions,
  getJobRecords,
  getPrintBatchList,
} from '../../../../utils/services/statusMonitoringAPI';
import { STATUS_MONITORING, JOB_QUEUE_MODAL, CONTROL_CENTER_MODAL } from '../TranslationValues';
import SwitchTabs from '../../../../components/Dashboard/SwitchTabs';
import { useSubscription } from '@apollo/client';
import { FETCH_FUNNEL_CHART } from '../../gql/FetchFunnelChart';
import FunnelChart from '../../FunnelChart';
import { metaSearchKeyMappings } from '../../../../utils/constants/statusMonitoring';

const { TabPane } = Tabs;

const namespaces = [
  'JOB_QUEUE_MODAL',
  'STATUS_MONITORING',
  'SEARCHABLE_TABLE',
  'CONTROL_CENTER_MODAL',
];

const STATIC_STATS_KEYS = ['uniq_id', 'uniq_name', 'language'];
const STATIC_STATS_KEYS_CONTROL_CENTER = ['uniq_id', 'uniq_name', 'language', 'status'];

const PrintingQueueModal = ({
  visible = false,
  onCancel = dummyCallback,
  title = 'Modal Title',
  t,
  activeItem,
  jobId,
  contentType,
  users,
}) => {
  const [statsFilter, setStatsFilter] = useState([]);
  const [tableSelectedFilters, setTableSelectedFilters] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(0);
  const loading = true;
  const [batchList, setBatchList] = useState([]);
  const [controlCenterCount, setControlCenterCount] = useState({ failure: 0, removed: 0 });
  const batchStats = activeItem?.batchStats || [];
  const { print: printTotal, email: emailTotal, hybrid: hybridTotal } = useMemo(
    () => getChannelTotals(batchStats),
    [batchStats],
  );
  const [activeTab, setActiveTab] = useState(() =>
    hybridTotal ? 2 : printTotal < 1 ? 1 : emailTotal < 1 ? 0 : 0,
  );
  const [currentTab, setCurrentTab] = useState(1);

  const isHybrid = activeItem?.batchType === 'HYBRID';

  const staticStatsKeys = isHybrid ? ['address'] : STATIC_STATS_KEYS;

  const activeChannel =
    activeTab === 0
      ? 'Documents'
      : activeTab === 1
      ? 'Email'
      : activeTab === 2
      ? 'Hybrid'
      : 'Documents';
  const userDetail = id => {
    const user = users.find(list => list.id === Number(id));
    return user.displayName || '';
  };
  const { print: unknownPrintCount, email: unknownEmailCount } = getChannelUnknownCount(
    activeItem?.batchStats || [],
  );

  const emailProcessedCount =
    activeItem?.batchStats
      ?.filter(stat => stat.channel === 'email')
      .reduce((acc, stat) => acc + parseInt(stat?.successCount), 0) || 0;

  const handleSelect = index => {
    if (selectedBatch !== index) {
      setSelectedBatch(index);
    }
  };

  const keyValueMapping = (auditData, type) => {
    return transformExclusionAndSearchKey(auditData, type, t);
  };

  const fetchPrintBatchList = async () => {
    if (visible && jobId) {
      try {
        const printStatus =
          contentType === PrintingQueue
            ? 'in_queue'
            : contentType === PrintingCenter
            ? 'completed'
            : null;
        const res = await getPrintBatchList(jobId, printStatus);
        if (res.statusCode === 5301 && res.data) {
          setBatchList(res.data);
        }
      } catch (error) {
        console.error('Error fetching print batch list:', error);
      }
    }
  };

  const fetchCountInfo = async () => {
    try {
      const { data: { exclusions = [] } = {} } = await getExclusions(jobId, 'print');

      const exclusionPayload = {
        limit: 1,
        page: 1,
        jobId,
        search: [
          { fieldName: 'status', value: 'success' },
          { fieldName: 'channel', value: 'print' },
        ],
        exclusions,
      };

      const failurePayload = {
        limit: 1,
        page: 1,
        jobId,
        search: [
          { fieldName: 'status', value: 'failure' },
          { fieldName: 'channel', value: activeTab === 0 ? 'print' : 'email' },
        ],
        exclusions: [],
      };

      const [exclusionRes, failureRes] = await Promise.all([
        exclusions.length ? fetchExclusionsData(exclusionPayload) : { dataCount: 0 },
        getJobRecords(failurePayload),
      ]);

      setControlCenterCount({
        failure: failureRes?.dataCount ?? 0,
        removed: exclusionRes?.dataCount ?? 0,
      });
    } catch (error) {
      console.error('Error fetching count info:', error);
    }
  };

  const channelTotals = useMemo(() => getChannelTotals(batchStats), [batchStats]);
  const { data } = useSubscription(FETCH_FUNNEL_CHART, {
    variables: {
      batchId: jobId,
    },
    skip: false,
    shouldResubscribe: true,
  });

  const funnelChartData = useMemo(() => {
    return updateFunnelChartData(data?.funnelChart, channelTotals.email, t);
  }, [data?.funnelChart]);
  const funnelChartColors = ['#2463eb', '#93b1f4', '#d4e1fc', '#133176'];

  useEffect(() => {
    if (visible && contentType !== ControlCenter) {
      fetchPrintBatchList();
    } else if (visible && contentType === ControlCenter) {
      fetchCountInfo();
    }

    setCurrentTab(1);
    setStatsFilter([]);
    setTableSelectedFilters([]);

    return () => {
      setBatchList([]);
    };
  }, [visible, activeTab]);

  const isDisabled = tabIndex => {
    if (tabIndex === 0) {
      return printTotal < 1;
    } else if (tabIndex === 1) {
      return emailTotal < 1;
    }
  };

  const keyNames = metaSearchKeyMappings(t);

  const combinedFilters = useMemo(() => {
    return [...statsFilter, ...tableSelectedFilters];
  }, [statsFilter, tableSelectedFilters]);

  const handleFunnelChartClick = selectedLabels => {
    const isAlreadySelected = combinedFilters.some(
      item =>
        item.key === keyNames.status && item.value.toLowerCase() === selectedLabels.toLowerCase(),
    );

    if (isAlreadySelected) {
      const customEvent = new CustomEvent('removeSearchFilter', {
        detail: { label: selectedLabels },
      });
      window.dispatchEvent(customEvent);

      setStatsFilter(prev =>
        prev.filter(
          item =>
            !(
              item.key === keyNames.status &&
              item.value.toLowerCase() === selectedLabels.toLowerCase()
            ),
        ),
      );
    } else {
      setStatsFilter(prev => [
        { key: keyNames.status, value: selectedLabels },
        ...prev.filter(item => item.key !== keyNames.status),
      ]);
    }
  };

  return (
    <ReusableModal
      visible={visible}
      title={title}
      onCancel={() => onCancel(false)}
      width="90%"
      styleProps={{
        top: 'auto',
        margin: '0 auto',
      }}
      bodyStyleProps={{
        padding: '20px',
        overflow: 'auto',
      }}
      footer={null}
      centered
      maskClosable={true}
      className="job-queue right-aligned-modal"
      transitionName={'ant-modal-slide-in'}
    >
      <JobQueueContainer>
        {!isHybrid && (
          <>
            {[PrintingQueue, PrintingCenter].includes(contentType) && (
              <JobQueueCard width={'30%'}>
                <div className="middleContainer">
                  <CardTitle textAlign={'center'}>
                    {t(
                      JOB_QUEUE_MODAL.PRINT_BATCH_DETAILS.key,
                      JOB_QUEUE_MODAL.PRINT_BATCH_DETAILS.value,
                    )}
                  </CardTitle>
                  <PrintBatchDetails>
                    {!batchList.length && (
                      <div className="emptyList">
                        <Empty
                          description={t(
                            JOB_QUEUE_MODAL.EMPTY_PRINT_BATCH.key,
                            JOB_QUEUE_MODAL.EMPTY_PRINT_BATCH.value,
                          )}
                        />
                      </div>
                    )}
                    {batchList.map((list, index) => {
                      return (
                        <BatchList
                          onClick={() => {
                            handleSelect(index);
                          }}
                          selected={index === selectedBatch}
                        >
                          <div style={{ display: 'flex', columnGap: '10px', marginBottom: '10px' }}>
                            <div>
                              <Avatar shape="square" icon="user" size="large">
                                PB
                              </Avatar>
                            </div>
                            <div style={{ lineHeight: 'normal' }}>
                              <div className="user-details"> {userDetail(list.createdBy)}</div>
                              <p className="date"> {list.createdOn}</p>
                            </div>

                            <div></div>
                          </div>
                          <TaglistDetails>
                            {!keyValueMapping(list.search, 'search').length &&
                              !keyValueMapping(list.exclusions, 'exclusions').length && (
                                <span>
                                  {t(
                                    JOB_QUEUE_MODAL.EMPTY_SEARCH_EXCLUSION.key,
                                    JOB_QUEUE_MODAL.EMPTY_SEARCH_EXCLUSION.value,
                                  )}
                                </span>
                              )}
                            {keyValueMapping(list.search, 'search').length ? (
                              <SearchExclusionFilters>
                                {`${t(
                                  JOB_QUEUE_MODAL.SEARCH_FILTER_LABEL.key,
                                  JOB_QUEUE_MODAL.SEARCH_FILTER_LABEL.value,
                                )}:`}
                                <div className="tagsContent">
                                  {keyValueMapping(list.search, 'search').map(
                                    ({ fieldName, value }, key) => (
                                      <div key={`search-${key}`} className="filtered-key">
                                        {fieldName} : {value}
                                      </div>
                                    ),
                                  )}
                                </div>
                              </SearchExclusionFilters>
                            ) : null}
                            {keyValueMapping(list.exclusions, 'exclusions').length ? (
                              <SearchExclusionFilters>
                                {`${t(
                                  JOB_QUEUE_MODAL.REMOVED_FILTER_LABEL.key,
                                  JOB_QUEUE_MODAL.REMOVED_FILTER_LABEL.value,
                                )}:`}
                                <div className="container">
                                  {keyValueMapping(list.exclusions, 'exclusions').map(group => (
                                    <div className="tagsContent">
                                      {group.map(({ fieldName, value }, key) => (
                                        <div key={`exclusions-${key}`} className="filtered-key">
                                          {fieldName} : {value}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              </SearchExclusionFilters>
                            ) : null}
                          </TaglistDetails>
                          <div className="totalCount">
                            {t(
                              JOB_QUEUE_MODAL.TOTAL_DOC_COUNT.key,
                              JOB_QUEUE_MODAL.TOTAL_DOC_COUNT.value,
                              { count: list.documentCount || 0 },
                            )}
                          </div>
                        </BatchList>
                      );
                    })}
                  </PrintBatchDetails>
                </div>
              </JobQueueCard>
            )}
            {[ControlCenter].includes(contentType) && (
              <JobQueueCard width={'30%'} alignCenter>
                <SwitchTabs
                  items={[
                    t(
                      STATUS_MONITORING.CHANNEL_DOCUMENT.key,
                      STATUS_MONITORING.CHANNEL_DOCUMENT.value,
                    ),
                    t(STATUS_MONITORING.CHANNEL_EMAIL.key, STATUS_MONITORING.CHANNEL_EMAIL.value),
                  ]}
                  width={'260px'}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isDisabled={isDisabled}
                />
                <div className="count-card-container">
                  <div className="count-card">
                    <div className="card-content">
                      <span>{activeTab === 0 ? printTotal : emailTotal}</span>
                      <p>
                        {t(
                          CONTROL_CENTER_MODAL.REQUEST_RECEIVED_COUNT.key,
                          CONTROL_CENTER_MODAL.REQUEST_RECEIVED_COUNT.value,
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="count-card">
                    <div className="card-content">
                      <span>
                        {activeTab === 0
                          ? activeItem?.printCompleteCount || 0
                          : emailProcessedCount}
                      </span>
                      <p>
                        {activeTab === 0
                          ? t(
                              CONTROL_CENTER_MODAL.PRINTED_COUNT.key,
                              CONTROL_CENTER_MODAL.PRINTED_COUNT.value,
                            )
                          : t(
                              CONTROL_CENTER_MODAL.PROCESSED_COUNT.key,
                              CONTROL_CENTER_MODAL.PROCESSED_COUNT.value,
                            )}
                      </p>
                    </div>
                  </div>

                  <div className="count-card">
                    <div className="card-content">
                      <span>{controlCenterCount.failure}</span>
                      <p>
                        {t(
                          CONTROL_CENTER_MODAL.FAILURE_COUNT.key,
                          CONTROL_CENTER_MODAL.FAILURE_COUNT.value,
                        )}
                      </p>
                    </div>
                  </div>

                  {activeTab === 0 && (
                    <div className="count-card">
                      <div className="card-content">
                        <span>{controlCenterCount.removed}</span>
                        <p>
                          {t(
                            CONTROL_CENTER_MODAL.REMOVED_RECORDS_COUNT.key,
                            CONTROL_CENTER_MODAL.REMOVED_RECORDS_COUNT.value,
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="count-card">
                    <div className="card-content">
                      <span>{activeTab === 0 ? unknownPrintCount : unknownEmailCount}</span>
                      <p>
                        {t(
                          CONTROL_CENTER_MODAL.UNKNOWN_COUNT.key,
                          CONTROL_CENTER_MODAL.UNKNOWN_COUNT.value,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                {activeTab === 1 && currentTab === 1 && (
                  <div style={{ width: '100%' }}>
                    <FunnelChart
                      funnelChartData={funnelChartData}
                      funnelChartColors={funnelChartColors}
                      totalEmailConversions={Math.round(data?.funnelChart?.conversionRate || 0)}
                      clickable
                      onChange={handleFunnelChartClick}
                      selected={
                        combinedFilters
                          .filter(item => item.key === keyNames.status)
                          .map(item => item.value) || []
                      }
                      maskColor={'#f6f6f6'}
                      defaultHeight={'fit-content'}
                    />
                  </div>
                )}
              </JobQueueCard>
            )}
          </>
        )}
        <JobQueueCard width={isHybrid ? '100%' : '70%'}>
          <div className="rightcontainer">
            {isEmptyObject(activeItem) === loading ? (
              <Skeleton active />
            ) : (
              <>
                {activeTab === 1 ? (
                  <Tabs
                    defaultActiveKey="1"
                    centered
                    className="email-records-tabs"
                    onChange={e => setCurrentTab(Number(e))}
                  >
                    <TabPane tab="Email Records" key="1">
                      <SearchableTable
                        jobId={jobId}
                        visible={visible}
                        contentType={'JobProcessingModal'}
                        defaultColumnCount={2}
                        statsFilter={statsFilter}
                        setStatsFilter={setStatsFilter}
                        staticStatsKeys={STATIC_STATS_KEYS_CONTROL_CENTER}
                        activeChannel={activeChannel}
                        onSelectedFiltersChange={setTableSelectedFilters}
                        isControlCenter={true}
                        chartCallBack={handleFunnelChartClick}
                      />
                    </TabPane>
                    <TabPane
                      tab={t(
                        CONTROL_CENTER_MODAL.UNKNOWN_RECORDS_LABEL.key,
                        CONTROL_CENTER_MODAL.UNKNOWN_RECORDS_LABEL.value,
                      )}
                      key="2"
                    >
                      <SearchableTable
                        jobId={jobId}
                        visible={visible}
                        contentType={contentType}
                        defaultColumnCount={2}
                        statsFilter={statsFilter}
                        setStatsFilter={setStatsFilter}
                        statsKeys={[]}
                        chartCallBack={() => {}}
                        staticStatsKeys={staticStatsKeys}
                        printBatchId={batchList[selectedBatch]?.id}
                        activeChannel={activeChannel}
                      />
                    </TabPane>
                  </Tabs>
                ) : (
                  <>
                    {contentType === ControlCenter && (
                      <div className="rightContainerTitle">
                        {t(
                          CONTROL_CENTER_MODAL.UNKNOWN_RECORDS_LABEL.key,
                          CONTROL_CENTER_MODAL.UNKNOWN_RECORDS_LABEL.value,
                        )}
                      </div>
                    )}
                    <SearchableTable
                      jobId={jobId}
                      visible={visible}
                      contentType={contentType}
                      defaultColumnCount={2}
                      statsFilter={statsFilter}
                      setStatsFilter={setStatsFilter}
                      statsKeys={[]}
                      chartCallBack={() => {}}
                      staticStatsKeys={staticStatsKeys}
                      printBatchId={batchList[selectedBatch]?.id}
                      activeChannel={activeChannel}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </JobQueueCard>
      </JobQueueContainer>
    </ReusableModal>
  );
};

export default withTranslation(namespaces)(PrintingQueueModal);
